// Remaining application views: admin, reports, self-service,
// salesforce, lawyer workbench, metrics, custom dashboards.
//
// This file holds the bulk of the per-role landing pages and
// admin-only management UIs. Future sessions may split this further
// (e.g. admin/, reports/) but for now they live together as the
// last extraction batch before the App entry point.

import { useState, useEffect, useMemo, useRef, useCallback, CSSProperties } from "react";
import { T } from "../theme";
import { fmtDate, today, capitalize, hasPermission } from "../utils";
import { apiCall } from "../apiClient";
import { useClientScope } from "../contexts/ClientScopeContext";
import { DEPARTMENTS, STATUS_COLORS } from "../constants";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "../salesforce";
import { renderMarkdown } from "./markdown";
import type { RequestRow, ContractRow, UserRow } from "../types/api";
import { Btn, Inp, Sel, Modal, Card, Badge, ConfBadge, SectionTitle, SearchBar, EmptyState, RefreshBtn, TabBar, ChatExportButtons, VoiceInput } from "./primitives";
import { DashboardView } from "./Dashboards";
import { ExtractionDashView, SecurityView, ValidationView } from "./AiViews";
import { SsoProvidersView } from "./SsoProvidersView";
import { MetricsView } from "./MetricsView";
import { CustomDashboardView } from "./CustomDashboardView";
import { ReportBuilder } from "./ReportBuilder";
import { InHouseReportsSection } from "./InHouseReportsSection";

export const ReportsView = ({ user = null }: { user?: UserRow | null }) => {
  const { edition: scopeEdition } = useClientScope();
  const [reports, setReports] = useState<any[]>([]);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [runLoading, setRunLoading] = useState(false);
  const [runtimeFilters, setRuntimeFilters] = useState<Record<string, any>>({});
  const [sortCol, setSortCol] = useState<any>(null);
  const [sortDir, setSortDir] = useState("asc");
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState("reports");
  const [pinnedIds, setPinnedIds] = useState<any[]>([]);
  const [shareLink, setShareLink] = useState<any>(null);

  useEffect(() => {
    apiCall("/reports").then(d => setReports(d.reports || [])).catch(() => {});
    apiCall("/reports/user/pinned").then(d => setPinnedIds(d?.pinned || [])).catch(() => {});
  }, []);

  const runReport = async (report: any) => {
    setSelectedReport(report);
    setRunLoading(true);
    setReportData(null);
    setSortCol(null);
    try {
      const d = await apiCall(`/reports/${report.id}/run`, { method: "POST", body: JSON.stringify({ filters: runtimeFilters }) });
      setReportData(d);
    } catch { setReportData({ error: true }); }
    setRunLoading(false);
  };

  const exportReport = async (format: string) => {
    if (!selectedReport) return;
    try {
      const res = await fetch(`/api/reports/${selectedReport.id}/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("kontractai_token")}` },
        body: JSON.stringify({ format }),
      });
      if (format === "xlsx" || format === "csv" || format === "pdf") {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const ext = format === "csv" ? "xlsx" : format;
        const a = document.createElement("a"); a.href = url; a.download = `${selectedReport.name.replace(/[^a-zA-Z0-9]/g, "_")}.${ext}`; a.click(); URL.revokeObjectURL(url);
      } else {
        const d = await res.json();
        const blob = new Blob([JSON.stringify(d.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${selectedReport.name.replace(/[^a-zA-Z0-9]/g, "_")}.json`; a.click(); URL.revokeObjectURL(url);
      }
    } catch { alert("Export failed"); }
  };

  const deleteReport = async (id: string) => {
    if (!window.confirm("Delete this report?")) return;
    await apiCall(`/reports/${id}`, { method: "DELETE" });
    setReports(prev => prev.filter((r: any) => r.id !== id));
    if (selectedReport?.id === id) { setSelectedReport(null); setReportData(null); }
  };

  const togglePin = async (id: string) => {
    try {
      const d = await apiCall("/reports/user/pinned", { method: "POST", body: JSON.stringify({ report_id: id }) });
      if (d && d.pinned === true) setPinnedIds(prev => [...prev, id]);
      else if (d && d.pinned === false) setPinnedIds(prev => prev.filter((p: any) => p !== id));
    } catch {}
  };

  const shareReport = async (id: string) => {
    try {
      const d = await apiCall("/reports/share", { method: "POST", body: JSON.stringify({ report_id: id, expires_days: 30 }) });
      const url = `${window.location.origin}/shared/report/${d.token}`;
      setShareLink(url);
      navigator.clipboard?.writeText(url);
    } catch { alert("Failed to create share link"); }
  };

  const cloneReport = async (id: string) => {
    try {
      const d = await apiCall("/reports/clone", { method: "POST", body: JSON.stringify({ report_id: id }) });
      if (d.report) setReports(prev => [...prev, d.report]);
    } catch { alert("Failed to clone report"); }
  };

  const handleDrillThrough = (reportId: string) => {
    setActiveTab("reports");
    const report = reports.find(r => r.id === reportId);
    if (report) runReport(report);
  };

  const grouped = reports.reduce((acc: any, r: any) => { (acc[r.category || ''] = acc[r.category || ''] || []).push(r); return acc; }, {});

  const sortedData = reportData?.data ? [...reportData.data].sort((a: any, b: any) => {
    if (!sortCol) return 0;
    const av = a[sortCol], bv = b[sortCol];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === "asc" ? cmp : -cmp;
  }) : [];

  const fmtCell = (val: any) => {
    if (val === null || val === undefined) return "—";
    if (val === true) return "Yes";
    if (val === false) return "No";
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}/.test(val)) return val.split("T")[0];
    return String(val);
  };

  const inp: CSSProperties = { padding: "8px 12px", border: `1px solid ${T.brd}`, borderRadius: 6, fontSize: 14, fontFamily: "inherit", width: "100%" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 22 }}>Reports</h2>
          <div style={{ display: "flex", gap: 2, backgroundColor: T.bg2, borderRadius: 6, padding: 2 }}>
            {["reports", "metrics", "dashboard"].map(t => (
              <button key={t} onClick={() => setActiveTab(t)} style={{ padding: "5px 14px", borderRadius: 5, border: "none", fontSize: 12, fontWeight: activeTab === t ? 600 : 400, backgroundColor: activeTab === t ? "#fff" : "transparent", color: activeTab === t ? T.tx : T.txM, cursor: "pointer", fontFamily: "inherit", boxShadow: activeTab === t ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>{t === "reports" ? "Reports" : t === "metrics" ? "Metrics" : "Dashboard"}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(hasPermission(user, 'reports.executive_pack') || ["admin", "legalops", "executive"].includes(user?.role || '')) && (
            <button onClick={async () => {
              try {
                const res = await fetch("/api/reports/executive-pack", { headers: { Authorization: `Bearer ${localStorage.getItem("kontractai_token")}` } });
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a"); a.href = url; a.download = `KontractAI_Executive_Pack_${new Date().toISOString().split("T")[0]}.pdf`; a.click(); URL.revokeObjectURL(url);
              } catch { alert("Failed to generate executive pack"); }
            }} style={{ padding: "8px 16px", backgroundColor: T.prp, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "inherit" }}>
              Executive Pack
            </button>
          )}
          {activeTab === "reports" && (hasPermission(user, 'reports.create') || ["admin", "legalops"].includes(user?.role || '')) && (
            <button onClick={() => setShowCreate(!showCreate)} style={{ padding: "8px 16px", backgroundColor: showCreate ? T.txM : T.acc, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13, fontFamily: "inherit" }}>
              {showCreate ? "Cancel" : "+ New Report"}
            </button>
          )}
        </div>
      </div>

      {activeTab === "reports" && scopeEdition === "in_house" && <InHouseReportsSection />}

      {activeTab === "metrics" && <MetricsView user={user} onDrillThrough={handleDrillThrough} />}

      {activeTab === "dashboard" && <CustomDashboardView user={user} />}

      {activeTab === "reports" && showCreate && <ReportBuilder onSave={async (def: any) => {
        try {
          const d = await apiCall("/reports", { method: "POST", body: JSON.stringify(def) });
          if (d.report) { setReports(prev => [...prev, d.report]); setShowCreate(false); }
        } catch { alert("Failed to create report"); }
      }} onCancel={() => setShowCreate(false)} />}

      {activeTab === "reports" && <div style={{ display: "flex", gap: 20 }}>
        {/* Report list sidebar */}
        <div style={{ width: 300, flexShrink: 0 }}>
          {/* Pinned reports section */}
          {pinnedIds.length > 0 && (() => {
            const pinned = reports.filter(r => pinnedIds.includes(r.id));
            return pinned.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.ylw, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, padding: "0 8px" }}>Pinned</div>
                {pinned.map(r => (
                  <div key={r.id} onClick={() => runReport(r)} style={{ padding: "10px 12px", borderRadius: 6, cursor: "pointer", backgroundColor: selectedReport?.id === r.id ? T.accS : T.ylwS, border: `1px solid ${selectedReport?.id === r.id ? T.acc : T.ylw}`, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{r.name}</div>
                    <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); togglePin(r.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.ylw, fontSize: 14, padding: 2 }} title="Unpin">&#9733;</button>
                  </div>
                ))}
              </div>
            ) : null;
          })()}
          {Object.entries(grouped).map(([cat, reps]: [string, any]) => (
            <div key={cat} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.txM, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, padding: "0 8px" }}>{cat}</div>
              {reps.map((r: any) => (
                <div key={r.id} onClick={() => runReport(r)} style={{ padding: "10px 12px", borderRadius: 6, cursor: "pointer", backgroundColor: selectedReport?.id === r.id ? T.accS : "#fff", border: `1px solid ${selectedReport?.id === r.id ? T.acc : T.brd}`, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>{r.name}{r.schedule && <span style={{ fontSize: 9, backgroundColor: T.accS, color: T.acc, padding: "1px 5px", borderRadius: 4, fontWeight: 600 }}>{r.schedule}</span>}</div>
                    {r.description && <div style={{ fontSize: 11, color: T.txM, marginTop: 2 }}>{r.description}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                    <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); togglePin(r.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: pinnedIds.includes(r.id) ? T.ylw : T.txD, fontSize: 13, padding: 2 }} title={pinnedIds.includes(r.id) ? "Unpin" : "Pin"}>{pinnedIds.includes(r.id) ? "\u2605" : "\u2606"}</button>
                    {(hasPermission(user, 'reports.create') || ["admin", "legalops"].includes(user?.role || '')) && <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); cloneReport(r.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: T.txD, fontSize: 11, padding: 2 }} title="Clone">&#x2398;</button>}
                    {!r.is_system && (hasPermission(user, 'reports.delete') || user?.role === "admin") && (
                      <button onClick={(e: React.MouseEvent) => { e.stopPropagation(); deleteReport(r.id); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626", fontSize: 14, padding: 2 }} title="Delete">×</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {reports.length === 0 && <div style={{ color: T.txM, fontSize: 14, padding: 12 }}>No reports available</div>}
        </div>

        {/* Report results */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {runLoading && <div style={{ textAlign: "center", padding: 40, color: T.txM }}>Running report...</div>}
          {reportData && !runLoading && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{reportData.report?.name}</h3>
                  <div style={{ fontSize: 12, color: T.txM }}>{reportData.totalRows} row{reportData.totalRows !== 1 ? "s" : ""}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button onClick={() => shareReport(selectedReport.id)} style={{ padding: "6px 12px", backgroundColor: "#fff", border: `1px solid ${T.blu}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", color: T.blu }}>Share</button>
                  <button onClick={() => exportReport("xlsx")} style={{ padding: "6px 12px", backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Excel</button>
                  <button onClick={() => exportReport("json")} style={{ padding: "6px 12px", backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>JSON</button>
                  <button onClick={() => exportReport("pdf")} style={{ padding: "6px 12px", backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>PDF</button>
                </div>
              </div>

              {/* Share link banner */}
              {shareLink && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", backgroundColor: T.bluS, border: `1px solid ${T.blu}`, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: T.blu }}>Share link copied!</span>
                  <input value={shareLink} readOnly style={{ flex: 1, padding: "4px 8px", border: `1px solid ${T.brd}`, borderRadius: 4, fontSize: 11, fontFamily: "monospace" }} onClick={e => (e.target as HTMLInputElement).select()} />
                  <button onClick={() => setShareLink(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.txM, fontSize: 14 }}>×</button>
                </div>
              )}

              {/* Runtime filter */}
              {reportData.data?.length > 0 && (
                <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  <input style={{ ...inp, width: 200 }} placeholder="Filter column..." onChange={e => {
                    const v = e.target.value;
                    if (!v) { setRuntimeFilters({}); return; }
                    const cols = Object.keys(reportData.data[0]);
                    const f: Record<string, any> = {};
                    cols.forEach((c: any) => { f[c] = v; });
                    setRuntimeFilters(f);
                  }} />
                  <button onClick={() => runReport(selectedReport)} style={{ padding: "6px 14px", backgroundColor: T.acc, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit" }}>Apply</button>
                </div>
              )}

              {/* Bar summary for bar-type reports */}
              {reportData.report?.report_type === "bar" && reportData.data?.length > 0 && (() => {
                const labelKey = Object.keys(reportData.data[0]).find(k => typeof reportData.data[0][k] === "string") || Object.keys(reportData.data[0])[0];
                const valKey = Object.keys(reportData.data[0]).find(k => typeof reportData.data[0][k] === "number") || "count";
                const maxVal = Math.max(...reportData.data.map((r: any) => Number(r[valKey]) || 0), 1);
                return (
                  <div style={{ marginBottom: 20, backgroundColor: "#fff", border: `1px solid ${T.brd}`, borderRadius: 8, padding: 16 }}>
                    {reportData.data.map((r: any, i: number) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                        <div style={{ width: 140, fontSize: 13, fontWeight: 500, textAlign: "right", flexShrink: 0 }}>{r[labelKey]}</div>
                        <div style={{ flex: 1, backgroundColor: T.bg2, borderRadius: 4, height: 24, overflow: "hidden" }}>
                          <div style={{ width: `${((Number(r[valKey]) || 0) / maxVal) * 100}%`, height: "100%", backgroundColor: T.acc, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 8, fontSize: 11, color: "#fff", fontWeight: 600, minWidth: 24 }}>
                            {r[valKey]}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Data table */}
              {sortedData.length > 0 && (
                <div style={{ overflowX: "auto", border: `1px solid ${T.brd}`, borderRadius: 8 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {(reportData.columns?.length > 0 ? reportData.columns : Object.keys(sortedData[0])).map((col: string) => (
                          <th key={col} onClick={() => { setSortCol(col); setSortDir(prev => sortCol === col && prev === "asc" ? "desc" : "asc"); }} style={{ padding: "10px 12px", textAlign: "left", borderBottom: `2px solid ${T.brd}`, backgroundColor: T.bg2, cursor: "pointer", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap", userSelect: "none" }}>
                            {col.replace(/_/g, " ")}{sortCol === col ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.map((row, i: number) => (
                        <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#fff" : T.bg2 }}>
                          {(reportData.columns?.length > 0 ? reportData.columns : Object.keys(row)).map((col: string) => (
                            <td key={col} style={{ padding: "8px 12px", borderBottom: `1px solid ${T.brd}`, whiteSpace: "nowrap", maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" }}>{fmtCell(row[col])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {sortedData.length === 0 && !runLoading && <div style={{ textAlign: "center", padding: 40, color: T.txM }}>No data returned</div>}
            </div>
          )}
          {!selectedReport && !runLoading && <div style={{ textAlign: "center", padding: 60, color: T.txM, fontSize: 15 }}>Select a report from the left to run it</div>}
        </div>
      </div>}
    </div>
  );
};

/* ═══════════════════ ROLE DASHBOARDS ═══════════════════ */
